import { useState, useRef, useCallback } from 'react';

import { Field } from '@components/utils/form';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import { isValidEmail } from '@utils/text';
import { SeturInputProps } from '@components/Input';

import Input from './Input';

const useEmailValidation = (callback?: (v: string) => void) => {
  const [email, setEmail] = useState<string>(''); // Store email as entered (preserve case)
  const [isValid, setIsValid] = useState(true);
  const tainted = useRef(!!email);
  useSkipEffectOnMount(() => {
    if (!tainted.current && email) {
      tainted.current = true;
    }
  }, [email]);
  const onChange = useCallback(
    (value: string) => {
      setEmail(value); // Save email in original format
      callback?.(value);
    },
    [callback]
  );
  return {
    email,
    isValid,
    onChange,
    validate: {
      onBlur: () => tainted.current && setIsValid(isValidEmail(email)),
    },
  };
};

type EmailProps = {
  InputComponent?: typeof Input;
  hasError?: boolean;
  errorMessage?: string;
} & SeturInputProps;

const TURKISH_CHARACTERS = [
  'İ',
  'ı',
  'ö',
  'Ö',
  'ü',
  'Ü',
  'ç',
  'Ç',
  'ğ',
  'Ğ',
  'ş',
  'Ş',
];
export const Email = ({
  InputComponent = Input,
  onChange: parentOnchange,
  ...rest
}: EmailProps) => {
  const { onChange, validate, email } = useEmailValidation(parentOnchange);
  return (
    <Field
      Component={InputComponent}
      placeholder='E-Posta*'
      onChange={onChange}
      errorPropName='error'
      value={email}
      {...validate}
      {...rest}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (TURKISH_CHARACTERS.includes(e.key)) e.preventDefault(); // Prevent Turkish chars
      }}
    />
  );
};

export default Email;
